import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { startIntake, brokerApplyUrl, intakeHandoffToken, handoffWords } from '@/services/intake.service.js';

// Broker deal-first intake launcher (MV-01 3.1, Ship 1b).
// Creates or attaches to a draft deal FIRST, then opens the /apply wizard in
// broker mode (deal-bound from keystroke one) in a new tab. Launched globally
// from the TopBar and account-scoped from the account workspace header
// (accountId/accountName preset the context).
export default function NewIntakeSheet({ open, onOpenChange, accountId = null, accountName = '', onSuccess }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [tail, setTail] = useState('');
  const [starting, setStarting] = useState(false);

  async function handleStart() {
    setStarting(true);
    try {
      const [result, handoff] = await Promise.all([
        startIntake({ accountId, name: name.trim(), nNumber: tail.trim() }),
        intakeHandoffToken().then((token) => ({ token }), (failed) => ({ failed })),
      ]);
      if (!result?.apply_url) throw new Error('No intake URL returned');
      if (!handoff.token) {
        toast.error(handoffWords(handoff.failed));
        return;
      }
      window.open(brokerApplyUrl(result.apply_url, handoff.token), '_blank', 'noopener');
      toast.success(
        result.attached
          ? 'Reopened the existing intake draft in a new tab'
          : accountId
            ? 'Intake opened in a new tab, deal-bound'
            : 'Intake opened in a new tab. The account workspace appears once the intake is submitted.'
      );
      onSuccess?.(result);
      onOpenChange?.(false);
      setName('');
      setTail('');
      // Only navigate to the account workspace when we have a real account id.
      // On a global launch accountId is null; navigating with the deal id in the
      // account slot 404s every /api/tracon/account/:idOrSlug/* endpoint.
      if (result?.deal_id && accountId) {
        navigate(`/account/${encodeURIComponent(accountId)}/deal/${encodeURIComponent(result.deal_id)}`);
      }
    } catch (err) {
      toast.error(`Failed to start intake: ${err.message || 'unknown error'}`);
    } finally {
      setStarting(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>New intake</SheetTitle>
          <SheetDescription>
            Creates the draft deal first, then opens the intake wizard in broker mode. Everything you type autosaves onto the deal.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {accountId ? (
            <div className="rounded border border-border bg-surface px-3 py-2 text-xs text-text-muted">
              Attached to account: <span className="font-medium text-text">{accountName || accountId}</span>
            </div>
          ) : null}

          <div>
            <label className="text-xs font-medium text-text-muted">Client name (optional)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Names the draft deal, prefills nothing"
              className="mt-1 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim"
              autoFocus
            />
          </div>

          <div>
            <label className="text-xs font-medium text-text-muted">Tail number (optional)</label>
            <input
              type="text"
              value={tail}
              onChange={(e) => setTail(e.target.value.toUpperCase())}
              placeholder="N12345"
              className="mt-1 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim"
            />
          </div>

          <Button onClick={handleStart} disabled={starting} className="w-full">
            {starting ? 'Creating draft deal…' : 'Start intake'}
          </Button>
          <p className="text-[11px] text-text-dim">
            The wizard opens in a new tab with the readiness meter rail. The deal workspace opens here.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
